<template>
  <div class="h-screen flex flex-col relative bg-gray-300 overflow-hidden">
    <transition name="fade">
      <div
        v-if="showForgotPassword"
        class="bg-black fixed bg-opacity-80 inset-0 z-50 h-screen w-full"
      >
        <div
          class="
            fixed
            center-h-v
            bg-white
            rounded-md
            sm:w-1/3
            w-full
            shadow-2xl
          "
        >
          <div v-if="loading" class="absolute inset-0 bg-black bg-opacity-50">
            <loader class="absolute center-h-v" />
          </div>
          <div
            class="flex justify-between items-center text-sm border-b-1.5 p-3"
          >
            <p>Forgot Password</p>
            <img
              v-if="!loading"
              class="
                h-5
                object-contain
                bg-buttonColor
                rounded-md
                cursor-pointer
                hover:shadow-lg
              "
              src="@/assets/icons/cross-white.png"
              @click="showForgotPassword = false"
            />
          </div>
          <form class="m-3" @submit.prevent="resetPassword">
            <div class="flex flex-col items-center justify-center">
              <p class="text-center font-medium text-sm text-primaryDark p-5">
                Provide your account's email for which you want to reset your
                password!
              </p>
              <input
                type="email"
                required
                v-model="resetPasswordEmail"
                placeholder="Email address"
                class="
                  border-0.5
                  text-xs
                  border-inputBorder
                  p-3
                  rounded
                  w-3/4
                  mb-6
                "
              />
            </div>
            <div class="flex justify-center">
              <button
                class="
                  bg-buttonColor
                  text-white
                  p-2
                  rounded
                  w-3/4
                  mb-6
                  hover:bg-primaryHover
                  focus:outline-none
                "
              >
                Reset password
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="loading"
        class="inset-0 absolute toppest bg-black bg-opacity-90 h-screen w-full"
      >
        <loader class="center-h-v" />
      </div>
    </transition>
    <div class="h-screen-10 w-full bg-navbarColor flex-shrink-0">
      <auth-nav-bar />
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-2 flex-1 relative items-center">
      <img
        src="@/assets/images/auth-phone.png"
        class="h-3/4 absolute center-h-v z-40 mt-12 hidden lg:block"
      />
      <div class="flex justify-center items-center px-6 py-10 lg:py-0">
        <div class="w-full max-w-xl bg-white shadow-xl rounded-3xl p-8 sm:p-12 text-textColorGray">
          <!-- <p class="text-xs sm:px-0 px-10">
            Welcome back, please login to your account
          </p> -->
          <div
            v-if="organization"
            class="
              flex
              items-center
              gap-5
              divide-x-0.5
              sm:px-0
              px-10
              divide-textColorGray
            "
          >
            <img class="h-10 sm:h-14 object-contain" :src="organization.logo" />
            <p class="px-3 sm:text-lg text-sm font-semibold">
              {{ organization.name }}
            </p>
          </div>

          <h1 class="text-lg sm:text-xl font-semibold py-5">
            Welcome to Lipalink, please login to your account
          </h1>
          <form
            class="w-full flex flex-col"
            @submit.prevent="login"
          >
            <transition name="fade">
              <div
                v-if="resetPasswordEmailSent"
                class="
                  text-xs
                  border-1.5 border-green-400
                  mb-6
                  flex
                  items-center
                  p-2
                  gap-4
                  sm:w-4/6
                "
              >
                <img class="h-5" src="@/assets/icons/check.png" />
                <p class="font-bold">
                  We have sent you an email. Follow the instructions outlined on
                  the email on how to reset your password.
                </p>
              </div>
            </transition>
            <transition name="fade">
              <div
                v-if="error"
                class="
                  text-xs
                  border-1.5 border-red-400
                  mb-6
                  flex
                  items-center
                  p-2
                  gap-4
                  sm:w-4/6
                "
              >
                <img class="h-5" src="@/assets/icons/cross.png" />
                <p class="font-bold">{{ error }}</p>
              </div>
            </transition>
            <input
              v-model="info.usernameOrEmail"
              placeholder="Username"
              type="text"
              required
              class="
                border-0.5
                text-sm
                font-semibold
                border-inputBorder
                p-3
                rounded
                w-full
                mb-6
              "
            />
            <div class="flex items-center gap-5 border-0.5 p-3 rounded border-inputBorder mb-2">
              <input
                v-model="info.password"
                required
                :type="passwordVisible ? 'text' : 'password'"
                placeholder="Password"
                class="text-xs font-semibold w-full focus:outline-none"
              />
              <button
                type="button"
                @click="togglePasswordVisibility"
                class="h-8 w-8 flex items-center justify-center rounded text-buttonColor hover:text-primary focus:outline-none"
                aria-label="Toggle password visibility"
                :aria-pressed="passwordVisible"
              >
                <svg v-if="!passwordVisible" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 012.182-3.568m2.796-2.797A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-4.188 5.182M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 3l18 18" />
                </svg>
              </button>
            </div>
            <div class="text-xs py-6 pl-1 flex">
              <!-- <input type="checkbox" /> Remember me -->
              <p
                @click="showForgotPassword = true"
                class="underline cursor-pointer hover:text-buttonColor"
              >
                Forgot Password?
              </p>
            </div>
            <button
              type="submit"
              class="
                bg-buttonColor
                hover:bg-primary
                text-white
                sm:w-4/6
                p-2
                sm:p-3
                mt-0
                sm:mt-6
                rounded
                font-semibold
                text-sm
              "
            >
              LOGIN
            </button>

            <!-- <p
              @click="goToEnroll"
              class="
                text-sm
                mt-5
                cursor-pointer
                text-buttonColor
                hover:text-black
              "
            >
              Enroll your organization?
            </p> -->
          </form>
        </div>
      </div>
      <div
        class="
          bg-gradient
          min-h-full
          relative
          overflow-hidden
          flex flex-col
          justify-center
        "
      >
        <div class="sm:pl-40 pl-10 sm:pr-20 pr-10 pb-20 mt-2">
          <text-slider></text-slider>
        </div>
        <!-- <div class="flex justify-center">
          <button
            @click="goToEnroll"
            class="
              bg-white
              py-2
              px-6
              sm:block
              hidden
              rounded-full
              font-semibold
              shadow-lg
            "
          >
            Enroll Now!
          </button>
        </div> -->

        <div class="absolute bottom-0 w-full mb-8 text-white">
          <p class="text-center text-xs opacity-70">
            &copy; Copyright {{ new Date().getFullYear() }}.
            <a class="text-black" href="sepia.co.ke">Sepia consulting</a> All
            Rights Reserved
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import TextSlider from "../../components/auth/textSlider.vue";
import AuthNavBar from "../../components/auth/authNavbar.vue";
//var qs = require("qs");
import Loader from "../../components/loader/loader.vue";
export default {
  name: "Login",
  components: { AuthNavBar, Loader, TextSlider },
  data() {
    return {
      info: {
        usernameOrEmail: this.$route.query.username,
        password: this.$route.query.password,
        // grant_type: "password",
      },
      organization: null,
      resetPasswordEmail: null,
      resetPasswordEmailSent: false,
      loading: false,
      showForgotPassword: false,
      error: null,
      passwordVisible: false,
    };
  },
  created() {
    // Seed store organization from route if present and store is empty
    const routeOrg = this.$route.params.organization;
    const storeOrg = this.$store.getters.getOrganization;
    if (routeOrg && !storeOrg) {
      this.$store.commit("setOrganization", routeOrg);
    }
    if (this.info.usernameOrEmail && this.info.password) {
      this.login();
    }
    // Do not fetch organization on initial load; this will be done
    // after successful authentication based on the authenticated user.
  },
  methods: {
    /**
     * Fetch organization details using the token from the store (set after login).
     * Uses GET /api/v1/organization/{organization}. Expects an organization key/slug
     * (e.g. from login response Data.username or Data.organization when API returns it).
     * Returns a promise so callers (e.g. login) can chain navigation logic.
     */
    getOrganization(orgKey) {
      if (!orgKey) {
        return Promise.reject(new Error("Organization identifier is missing."));
      }

      const token = this.$store.getters.getToken;
      const options = {
        headers: {
          Accept: "text/plain",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      };

      return axios.get(`organization/${encodeURIComponent(orgKey)}`, options).then(
        (res) => {
          // New API shape: { success, message, Data: { id, username, organizationName, organizationAddress, contact, email, logoUrl } }
          const raw = res.data || {};
          const data = raw.Data || raw;
          this.organization = {
            name: data.organizationName ?? data.name ?? orgKey,
            logo: data.logoUrl ?? data.logo ?? null,
          };
          // Persist the organization key in the store (used for subsequent API calls and routes)
          const storeKey = data.username || orgKey;
          this.$store.commit("setOrganization", storeKey);

          if (storeKey === "admin") {
            document.title = `Lipa Link | Admin`;
          } else {
            document.title = `Lipa Link | ${this.organization.name}`;
          }

          return res;
        }
      );
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    goToEnroll() {
      this.$router.push({
        name: "enroll",
      });
    },
    resetPassword() {
      const org =
        this.$store.getters.getOrganization || this.$route.params.organization;
      if (!org) {
        this.error =
          "Organization is not set. Please access the login page via your organization link.";
        return;
      }
      this.loading = true;
      const options = {
        headers: {
          "Content-Type": "application/json",
          Accept: "text/plain",
        },
      };
      const payload = {
        email: this.resetPasswordEmail,
      };
      axios
        .post(`account/${encodeURIComponent(org)}/forgot-password`, payload, options)
        .then(
          () => {
            this.loading = false;
            this.showForgotPassword = false;
            this.resetPasswordEmailSent = true;
            setTimeout(() => {
              this.resetPasswordEmailSent = false;
            }, 10000);
          },
          (error) => {
            this.loading = false;
            this.error =
              error?.response?.data?.message ||
              error?.response?.data?.errors ||
              "Failed to send reset password email.";
          }
        );
    },
    login() {
      this.loading = true;
      this.error = null;
      const options = {
        headers: {
          "Content-Type": "application/json",
          "accept": "text/plain"
        },
      };
      // Prepare JSON payload matching API requirements
      const payload = {
        usernameOrEmail: this.info.usernameOrEmail,
        password: this.info.password
      };
      console.log("Login payload:", payload);
      axios
        .post("auth/login", payload, options)
        .then((response) => {
          // Store the authenticated user/token payload so getOrganization can use the token
          this.$store.commit("login", response.data);

          // Organization key for GET /api/v1/organization/{organization}: prefer API response, then
          // the username/email the user just typed (that value is what the org API expects), then store/route.
          const data = response.data || {};
          const userData = data.Data || data;
          const formUsername = (this.info.usernameOrEmail || "").trim();
          const orgKey =
            userData.organization ||
            userData.organizationSlug ||
            userData.organizationUsername ||
            userData.orgCode ||
            userData.username ||
            formUsername || // use login form username so org is from user identity, not URL
            this.$store.getters.getOrganization ||
            this.$route.params.organization;

          if (!orgKey) {
            throw { isOrgMissing: true };
          }

          // Fetch organization details with token (GET /api/v1/organization/{organization}); store result in getOrganization store
          return this.getOrganization(orgKey).then(() => response);
        })
        .then(() => {
          // At this point, both login and organization lookup have succeeded; org is in store
          this.loading = false;

          const org = this.$store.getters.getOrganization;
          const params = org ? { organization: org } : {};

          this.$router.replace({ name: "dashboard", params });
        })
        .catch((error) => {
          console.log(error);

          // Missing organization after successful auth – treat as 404
          if (error && error.isOrgMissing) {
            this.loading = false;
            this.$router.replace({ name: "NotFound" });
            return;
          }

          if (error && error.response) {
            // If organization lookup returns 404, also treat as 404
            if (error.response.status === 404) {
              this.loading = false;
              this.$router.replace({ name: "NotFound" });
              return;
            }

            this.error =
              error.response.data?.error_description ||
              error.response.data?.message ||
              "An error occurred while logging in.";
          } else {
            this.error = error?.message || "An error occurred while logging in.";
          }
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.toppest {
  z-index: 99999;
}
.center-h-v {
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}
.h-screen-10 {
  height: 10vh;
}
.h-screen-90 {
  height: 90vh;
}
.bg-gradient {
  background: rgb(209, 213, 219);
  background: -moz-linear-gradient(
    135deg,
    rgba(209, 213, 219, 1) 0%,
    rgba(148, 163, 184, 1) 100%
  );
  background: -webkit-linear-gradient(
    135deg,
    rgba(209, 213, 219, 1) 0%,
    rgba(148, 163, 184, 1) 100%
  );
  background: linear-gradient(
    135deg,
    rgba(209, 213, 219, 1) 0%,
    rgba(148, 163, 184, 1) 100%
  );
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#d1d5db",endColorstr="#94a3b8",GradientType=1);
}

/* Hide native password reveal/clear icons in various browsers */
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none !important;
}
input[type="password"]::-webkit-clear-button,
input[type="password"]::-webkit-textfield-decoration-container,
input[type="password"]::-webkit-textfield-decoration-button {
  display: none !important;
}
</style>

